import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useSelector, useDispatch } from 'react-redux';
import { Grid } from '@material-ui/core';
import store from 'store';
import MsgBox from 'components/MsgBox/MsgBox';
import CustomTable from 'components/CustomTable';
import CustomTextField from 'components/Input/CustomTextField';
import Popup from 'components/popup/popup';
import { EDIT_GLOBAL_VARIABLES, GET_GLOBAL_VARIABLES, LOAD_ALL_CONFIG } from 'store/key';
import LoadingIndicator from 'components/LoadingIndicator';
import { getSystemMessage } from 'store/system/systemMessageReducer';
import useDialog from 'components/ConfirmDialog/useDialog';
import { VALUE_DUPLICATED } from 'utility/frontendMessage';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    title: {
        marginRight: theme.spacing(4),
        fontWeight: 'bold',
    },
    tableContanier: {
        marginTop: theme.spacing(2),
    },
    item: {
        marginTop: theme.spacing(2),
    },
}));

const EditContent = (props) => {
    const classes = useStyles();
    const rowData = props.rowData ?? {};
    const [codeValue, setCodeValue] = useState(rowData.codeValue ?? '');
    const [codeValueValid, setCodeValueValid] = useState(false);
    const showDialog = useDialog();

    const checkIsDuplicated = () => {
        /** value not changed */
        if (rowData.codeValue === codeValue) return false;

        /** value is duplicated */
        return props.globalVariablesList?.filter(item => {
            return item.code === rowData.code && item.subCode === rowData.subCode && item.codeValue === codeValue;
        }).length > 0;
    };

    const confirm = () => {
        if (codeValueValid) {
            if (checkIsDuplicated()) {
                return showDialog({
                    description: VALUE_DUPLICATED,
                    disableCancelButton: true,
                });
            };

            const data = {
                ...rowData,
                codeValue: codeValue,
            };
            props.editRow(data);
            props.setOpenPopup(false);
        }
    };
    const cancel = () => {
        props.setOpenPopup(false);
    };
    const reset = () => {
        setCodeValue(rowData.codeValue ?? '');
    };
    return (
        <Popup
            height="30%"
            width="30%"
            title="Edit Global Variables"
            footerActions={[
                { label: 'Cancel', clicked: cancel, color: 'default' },
                { label: 'Reset', clicked: reset },
                { label: 'Confirm', clicked: confirm },
            ]}
            open={props.openPopup}
            closePopup={cancel}
        >
            <Grid container wrap="nowrap" direction="column">
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={3}>
                        Category:
                    </Grid>
                    <Grid container item xs={9}>
                        {rowData.code}
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={3}>
                        Key:
                    </Grid>
                    <Grid container item xs={9}>
                        {rowData.subCode}
                    </Grid>
                </Grid>
                <Grid container wrap="nowrap" alignItems="center" className={classes.item}>
                    <Grid container item xs={3}>
                        Value:
                    </Grid>
                    <Grid container item xs={9}>
                        <CustomTextField
                            label="Value"
                            name="codeValue"
                            value={codeValue}
                            onChange={(e) => setCodeValue(e.target.value)}
                            validators={['isRequired', 'disableChinese']}
                            isValidValue={(valid) => setCodeValueValid(valid)}
                            forceErrorDisplay={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Popup>
    );
};

const GlobalVariables = (props) => {
    const classes = useStyles();

    const dispatch = useDispatch();
    const globalVariablesList = useSelector((state) => state.globalVariables.globalVariablesList);
    const isLoading = useSelector((state) => state.globalVariables.isLoading);

    const columns = [
        { field: 'code', headerName: 'Category', width: 200 },
        { field: 'subCode', headerName: 'Key', width: 200 },
        { field: 'codeValue', headerName: 'Value', width: 480 },
    ];

    const [openPopup, setOpenPopup] = useState(false);
    const [warningMsg, setWarningMsg] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const getList = () => {
        dispatch({
            type: GET_GLOBAL_VARIABLES,
            payload: {},
        });
    };

    useEffect(() => {
        getList();
        // eslint-disable-next-line
    }, []);

    const handleEdit = (row) => {
        setSelectedRow(row);
        setOpenPopup(true);
    };
    const editRow = (row) => {
        store.dispatch({
            type: EDIT_GLOBAL_VARIABLES,
            payload: {
                id: row.id,
                data: {
                    codeValue: row.codeValue,
                },
                callback: () => {
                    dispatch({ type: LOAD_ALL_CONFIG });
                    getList();
                    setWarningMsg({
                        message: getSystemMessage('hasBeenUpdated', ['Global variable']),
                        footerActions: [
                            {
                                label: 'Confirm',
                                clicked: () => {
                                    setWarningMsg(null);
                                },
                            },
                        ],
                    });
                },
            },
        });
    };

    return (
        <Grid container wrap="nowrap" direction="column" className={classes.root}>
            {warningMsg ? (
                <MsgBox
                    type={warningMsg.type}
                    message={warningMsg.message}
                    footerActions={warningMsg.footerActions}
                    title={warningMsg.title}
                    closePopup={() => setWarningMsg(null)}
                />
            ) : null}
            {openPopup ? (
                <EditContent
                    editRow={editRow}
                    rowData={selectedRow}
                    openPopup={openPopup}
                    setOpenPopup={setOpenPopup}
                    globalVariablesList={globalVariablesList}
                />
            ) : null}
            <Grid container wrap="nowrap" alignItems="center">
                <Typography className={classes.title}>Edit Global Variables:</Typography>
            </Grid>
            <Grid className={classes.tableContanier}>
                {isLoading ? (
                    <LoadingIndicator />
                ) : (
                    <CustomTable
                        isEditAllow={true}
                        onEdit={handleEdit}
                        columns={columns}
                        rows={globalVariablesList}
                        allowResize
                        skipMaxHeight
                        fullWidthTable={false}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default GlobalVariables;
